import {
    Button,
    MenuItem,
    Modal,
    ModalActions,
    ModalContent,
    ModalTitle,
    modalClasses,
} from "@kaltura/ds-react-components";
import { postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { useRef, useState } from "react";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";
import { ButtonClickAnalyticsType, SharedMenuItemProps } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import styled from "@emotion/styled";

export interface RestoreActionProps extends SharedMenuItemProps {
    entryId: string;
    entryName: string;
    apiUrl: string;
}

const StyledModal = styled(Modal)({
    [`& .${modalClasses.paper}`]: {
        maxWidth: 584,
    },
});

/**
 * RestoreAction component is a menu item that allows users to restore a media entry from the recycle bin.
 * @data-endpoint recyclebin-recycle-bin-ds-restore
 */
export const RestoreAction = ({ entryId, onCloseMenu, autoFocus, reloadMedia, apiUrl }: RestoreActionProps) => {
    const [openConfirmation, setOpenConfirmation] = useState(false);
    const [processing, setProcessing] = useState(false);

    const pendingReload = useRef(false);

    const { showToast } = useToastsContext();
    const sendButtonAnalytics = useButtonAnalytics();

    const closeConfirmation = () => {
        setOpenConfirmation(false);
        onCloseMenu?.();
    };

    const handleClick = () => {
        setOpenConfirmation(true);
        sendButtonAnalytics(
            "Restore item",
            ButtonClickAnalyticsType.EDIT,
            entryId,
            "recycle bin",
            "Menu - Recycle bin - Item actions"
        );
    };

    const handleRestore = async () => {
        setProcessing(true);
        try {
            const response = await postKmsData(apiUrl, { ids: [entryId] });
            if (response.success) {
                showToast({
                    severity: "success",
                    message: translate("media has been restored"),
                });

                pendingReload.current = true;
                closeConfirmation();
            }
            else {
                showToast({ severity: "error", message: translate("Failed to restore media") });
            }
        }
        catch (e: unknown) {
            closeConfirmation();
            showToast({ severity: "error", message: translate("Failed to restore media") });
        }
        finally {
            sendButtonAnalytics(
                "Confirm restore item",
                ButtonClickAnalyticsType.SAVE,
                entryId,
                "recycle bin",
                "Modal - Recycle bin - Restore item"
            );
            setProcessing(false);
        }
    };

    return (
        <>
            <MenuItem onClick={handleClick} autoFocus={autoFocus}>
                {translate("Restore")}
            </MenuItem>

            <StyledModal
                open={openConfirmation}
                onClose={closeConfirmation}
                maxWidth={false}
                slotProps={{
                    transition: {
                        onExited: () => {
                            if (pendingReload.current) {
                                // reloadMedia causes the component to unmount while the modal's exit transition is still running
                                // so the transition never completes and the modal appears to stay open (or closes abruptly/incorrectly).
                                // Deferring reloadMedia to onExited ensures the unmount only happens after the transition is fully done.
                                pendingReload.current = false;
                                reloadMedia?.();
                            }
                        },
                    },
                }}
            >
                <ModalTitle>{translate("Restore item")}</ModalTitle>
                <ModalContent>
                    {translate(
                        "Are you sure you want to restore this media? It will be found where you originally published it."
                    )}
                </ModalContent>
                <ModalActions>
                    <Button variant={"borderless"} color={"secondary"} onClick={closeConfirmation}>
                        {translate("Cancel")}
                    </Button>
                    <Button color="primary" onClick={handleRestore} loading={processing} disabled={processing}>
                        {translate("Restore")}
                    </Button>
                </ModalActions>
            </StyledModal>
        </>
    );
};

export default RestoreAction;
